import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T, card, button } from './speedTheme';
import { preloadRuneImages } from './RuneStar';
import StatRuneSwitch from './StatRuneSwitch';
import RuneBuildPanel from './RuneBuildPanel';
import MonsterStatPanel from './MonsterStatPanel';
import { shortestName, PickerModal } from './speedShared';
import { buildIdIndex } from '../utils/swexRoster';
import { mobImgUrl } from '../utils/monsterCells';
import { saveAccount, loadAccount, clearAccount, saveRawAccount, clearRawAccount, rawAccountOf } from '../utils/runeStore';
import RuneFit from './RuneFit';
import { takeRuneFit, RuneFitSource } from '../utils/fitHandoff';
import {
    Rune, AccountBonus, Unit, runesFromAccount, unitsFromAccount, peakRune, relicMains, ARTIFACT_ATK_DEFAULT,
    SPEED_COMBOS, SpeedBuild, rankSpeed, setsAbove, thresholdsOf, MainPick, SLOT4_MAINS, SLOT6_MAINS, EFF,
    POWER_COMBOS, bestPowerBuild, powerOf, speedTotalOf, JOKER, PowerBase, ddjBuilds as ddjSolve
} from './runeEngine';

/** 전투력 셈에 쓸 몹의 밑값 — 몹을 못 고르면 조커 값으로 대신한다 */
const mobBaseOf = (u: Unit | null): PowerBase =>
    (u ? { atk: u.base.atk, critRate: u.base.critRate, critDmg: u.base.critDmg } : JOKER);

/** 탭마다 기본 몹 — 전투력은 풍조커(루셴), 최고속은 풍극지(티아나). 몹 표의 (가문, 속성) 열쇠다 */
const DEFAULT_MOB: Record<MobView, { family: string; attribute: string }> = {
    power: { family: 'Joker', attribute: 'Wind' },
    speed: { family: 'Polar Queen', attribute: 'Wind' }
};

/**
 * **드데조** (사용자 2026-10-21) — 점령전 드데조(풍조커·풍드나·빛데몬) 셋에 전투력을 나눠 맞춘다. 룬은 겹치지 않고,
 * 풍조커·빛데몬이 풍드나보다 느리지 않게, 셋의 전투력 합이 가장 높은 쪽(runeEngine `ddjBuilds`). 몹 표의 (가문, 속성) 열쇠다.
 */
const DDJ_MOBS: Array<{ family: string; attribute: string; label: string }> = [
    { family: 'Joker', attribute: 'Wind', label: '풍조커' },
    { family: 'Dragon Knight', attribute: 'Wind', label: '풍드나' },
    { family: 'Demon', attribute: 'Light', label: '빛데몬' }
];

/** 주옵 칩 이름 — 4번·6번 고르기 */
const MAIN_SHORT: Record<number, string> = {
    [EFF.HP_PCT]: '체%', [EFF.ATK_PCT]: '공%', [EFF.DEF_PCT]: '방%', [EFF.CRIT_RATE]: '치확', [EFF.CRIT_DMG]: '치피',
    [EFF.RESIST]: '효저', [EFF.ACCURACY]: '효적'
};

/** 탭에서 고른 몹 — 몹 표의 (가문, 속성) + 계정에서 찾은 개체(없으면 null) */
interface MobPick { family: string; attribute: string; unitId: number | null }

/**
 * **룬 옵티마이저 탭** — 계정 JSON 의 룬 전부로 "최고 전투력" 과 "세트별 최고속도" 를 찾는다.
 *
 * 파일은 브라우저에서만 읽고 어디에도 보내지 않는다(보유몹 탭의 json 업데이트와 같은 원칙).
 * 읽고 나면 **쓰는 것만 이 브라우저에 담아 둬**(IndexedDB, `utils/runeStore`) 다음에 열 때 파일을
 * 또 고르지 않아도 된다. [담아 둔 계정 지우기] 로 지운다.
 *
 * 두 갈래는 **전투력 | 최고속** 탭으로 나눈다. 둘 다 결과를 게임 룬 창처럼 그린다 — 왼쪽 별에
 * 여섯 자리, 판을 누르면 오른쪽에 그 룬의 창(`RuneBuildPanel`). 최고속은 신잡·신의·폭잡·폭의
 * 중 하나를 고르고 1~3순위를 넘겨 본다.
 */

/** fit = 룬피팅 — 전투기록·공략 룬작에 내 룬 맞추기 (RuneFit, 사용자 2026-10-27) */
type View = 'fit' | MobView;
/** 몹 하나를 골라 셈하는 탭 */
type MobView = 'power' | 'speed';
/** 현재 기준 = 현재 있는 룬 그대로 · 고점 기준 = 풀강화·보석·연마를 다 했을 때 */
type Basis = 'now' | 'peak';

/** 최고속 탭에서 고를 수 있는 조합 — 절잡·절의는 점령전에서 안 써서 뺐다 */
const SPEED_PICKS = SPEED_COMBOS.filter((c) => c.four !== 10);

export default function RunePage({ isMobile, monsterMapping }: { isMobile: boolean; monsterMapping?: any }) {
    const [runes, setRunes] = useState<Rune[] | null>(null);
    const [units, setUnits] = useState<Unit[]>([]);
    /** 탭마다 빌드를 끼워 볼 몹 — 초상을 누르면 고르기 창에서 바꾼다 */
    const [picks, setPicks] = useState<Record<MobView, MobPick | null>>({ power: null, speed: null });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [meta, setMeta] = useState<{ equipped: number; stored: number; wizardName: string | null; bonus: AccountBonus } | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [reading, setReading] = useState(false);
    /** 점선 칸 위로 파일을 끌고 있나 — 테두리를 진하게 해 알려 준다 */
    const [dragging, setDragging] = useState(false);
    /** 담아 둔 계정을 불러온 시각 — 화면에 "9월 22일에 읽은 것" 처럼 적는다 */
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const fileInput = useRef<HTMLInputElement | null>(null);

    /** 전투기록 창·공략의 [내 룬으로 맞추기]로 왔으면 룬피팅 탭으로 연다 (utils/fitHandoff) */
    const [fitSource, setFitSource] = useState<RuneFitSource | null>(() => takeRuneFit());
    const [view, setView] = useState<View>(() => (fitSource ? 'fit' : 'power'));
    // 이 화면이 떠 있는 채로 또 누른 경우 — 이벤트로 받는다
    useEffect(() => {
        const on = () => { const s = takeRuneFit(); if (s) { setFitSource(s); setView('fit'); } };
        window.addEventListener('swpvpdb:open-rune-fit', on);
        return () => window.removeEventListener('swpvpdb:open-rune-fit', on);
    }, []);
    const [basis, setBasis] = useState<Basis>('now');
    /** 드데조 켬 — 전투력 탭이 몹 하나 대신 셋을 맞춘다(DDJ_MOBS) */
    const [ddj, setDdj] = useState(false);
    /** 드데조에서 보고 있는 몹 — DDJ_MOBS 의 번호. 처음은 풍조커 */
    const [ddjPick, setDdjPick] = useState(0);

    /**
     * 신속 4벌은 기본속도의 25% 라 기본속도에 따라 답이 달라진다. 고르는 단추는 뺐고(사용자 지시)
     * 점령전에서 흔한 115 로 둔다 — 속도는 어차피 "룬 + 세트"(기본속도 제외)로 적는다.
     */

    const [comboKey, setComboKey] = useState(SPEED_PICKS[0].key);
    const [rank, setRank] = useState(0);
    /** 4번·6번 주옵 (사용자 2026-10-21 — "속체체" 처럼 자리마다 고른다). null 이면 아무거나 */
    const [mains, setMains] = useState<MainPick>({ 4: null, 6: null });

    // 룬 별 그림은 JSON 을 고르는 동안 미리 받아 둔다
    useEffect(() => { preloadRuneImages(); }, []);

    // 지난번에 담아 둔 계정이 있으면 그대로 연다
    useEffect(() => {
        let alive = true;
        loadAccount().then((a) => {
            if (!alive || !a) return;
            setRunes(a.runes);
            setUnits(a.units);
            setMeta({ equipped: a.equipped, stored: a.stored, wizardName: a.wizardName, bonus: a.bonus });
            setSavedAt(a.savedAt);
            setPicks({
                power: { ...DEFAULT_MOB.power, unitId: null },
                speed: { ...DEFAULT_MOB.speed, unitId: null }
            });
        });
        return () => { alive = false; };
    }, []);

    /**
     * 담아 둔 계정으로 열면 몹 표(monsterMapping)가 아직 안 와 있을 수 있다 — 그때는 기본 몹의
     * 개체를 못 찾는다. 표가 오면 한 번 더 찾아 준다.
     */
    useEffect(() => {
        if (!units.length || !monsterMapping) return;
        setPicks((cur) => {
            const next = { ...cur };
            let changed = false;
            for (const v of ['power', 'speed'] as MobView[]) {
                const p = cur[v];
                if (!p || p.unitId != null) continue;
                const id = ownedUnitId(units, p.family, p.attribute);
                if (id != null) { next[v] = { ...p, unitId: id }; changed = true; }
            }
            return changed ? next : cur;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, monsterMapping]);

    async function readFile(file: File) {
        // 고르기 창이 안 걸렀을 때를 대비해 여기서 한 번 더 — 7MB 를 읽고 나서 실패하면 기다린 게 아깝다
        if (!/\.json$/i.test(file.name)) {
            setProblem(`${file.name} 은 JSON 파일이 아닙니다 — SWEX 가 저장한 "계정이름-번호.json" 을 고르세요.`);
            if (fileInput.current) fileInput.current.value = '';
            return;
        }
        setReading(true);
        setProblem(null);
        try {
            const account = JSON.parse(await file.text());
            const r = runesFromAccount(account);
            const us = unitsFromAccount(account);
            setRunes(r.runes);
            setUnits(us);
            // 탭마다 기본 몹을 계정에서 찾아 둔다 (없으면 unitId 가 null — 초상은 보이고 스탯만 못 낸다)
            setPicks({
                power: { ...DEFAULT_MOB.power, unitId: ownedUnitId(us, DEFAULT_MOB.power.family, DEFAULT_MOB.power.attribute) },
                speed: { ...DEFAULT_MOB.speed, unitId: ownedUnitId(us, DEFAULT_MOB.speed.family, DEFAULT_MOB.speed.attribute) }
            });
            setMeta({ equipped: r.equipped, stored: r.stored, wizardName: r.wizardName, bonus: r.bonus });
            setSavedAt(Date.now());
            // 쓰는 것만 담아 둔다 — 다음에 열 때 파일을 또 고르지 않게
            saveAccount({
                savedAt: Date.now(), wizardName: r.wizardName, equipped: r.equipped, stored: r.stored,
                bonus: r.bonus, runes: r.runes, units: us
            });
            // 원본(룬·보유몹·아티)도 담는다 — 시뮬 탭 [내 룬 자동할당]이 이 계정을 쓴다 (사용자 2026-10-27 "json을 룬 옵티마이저에서 추가한거로")
            try { saveRawAccount(rawAccountOf(account)); } catch { /* 몹 목록이 없는 JSON — 옵티마이저는 그대로 쓴다 */ }
        } catch (e: any) {
            setProblem(`파일을 읽지 못했습니다 — ${e?.message || '알 수 없는 오류'}`);
        } finally {
            setReading(false);
            if (fileInput.current) fileInput.current.value = '';
        }
    }

    /**
     * 고점 기준이면 룬을 목표에 맞춰 끝까지 키운 것으로 바꿔 놓고 셈한다 — 전투력은 공%·치피 쪽으로,
     * 최고속은 공속 쪽으로 변환하니 탭마다 다른 룬 묶음이다.
     */
    const powerRunes = useMemo(() => (runes ? (basis === 'peak' ? runes.map((r) => peakRune(r, 'power')) : runes) : null), [runes, basis]);
    const speedRunes = useMemo(() => (runes ? (basis === 'peak' ? runes.map((r) => peakRune(r, 'speed')) : runes) : null), [runes, basis]);

    /** unit_master_id → (가문, 속성) — 몹 표로 */
    const idIndex = useMemo(() => buildIdIndex(monsterMapping), [monsterMapping]);

    /**
     * (가문, 속성)에 맞는 **계정의 몹** — 같은 몹이 여럿이면 레벨·성급 높은 것. 각성 전·후는
     * unit_master_id 가 다르지만 몹 표가 같은 (가문, 속성)으로 묶어 준다. 없으면 null.
     */
    function ownedUnitId(us: Unit[], family: string, attribute: string): number | null {
        // 같은 몹이 여럿이면 레벨·성급 다음엔 유물을 새긴 놈 (아티팩트는 늘 +200 으로 보므로 안 따진다)
        const owned = us
            .filter((u) => { const e = idIndex.get(u.unitMasterId); return e && e.family === family && e.attr === attribute; })
            .sort((a, b) => b.level - a.level || b.stars - a.stars || b.relics.length - a.relics.length);
        return owned[0]?.unitId ?? null;
    }
    const mobShort = (family: string, attribute: string) => shortestName(monsterMapping, family, attribute, family);
    const mobImage = (family: string, attribute: string) => {
        const byAttr = monsterMapping?.monsters?.[family]?.[attribute];
        const path = byAttr?.SecondAwakened?.filepath || byAttr?.Awakened?.filepath || byAttr?.Unawaken?.filepath;
        return path ? mobImgUrl(path) : null;
    };

    /** 지금 탭의 몹 */
    const pick = view === 'fit' ? null : picks[view];
    const unit = pick?.unitId != null ? (units.find((u) => u.unitId === pick.unitId) || null) : null;
    /** 전투력 탭의 몹 — 아티팩트는 늘 +200, 유물은 그 몹에 새긴 것의 공격력 % */
    const powerUnit = picks.power?.unitId != null ? (units.find((u) => u.unitId === picks.power!.unitId) || null) : null;
    const powerRelicAtkPct = powerUnit ? relicMains(powerUnit.relics).atkPct : 0;
    /**
     * 최고속 탭의 몹과 그 **기본속도**. 신속 4벌의 25% 가 여기에 걸린다 — 예전에는 115 로
     * 박혀 있어 몹을 바꿔도 값이 그대로였다. 몹을 못 고르면 115 로 대신한다.
     */
    const speedUnit = picks.speed?.unitId != null ? (units.find((u) => u.unitId === picks.speed!.unitId) || null) : null;
    const base = speedUnit?.base.spd ?? 115;

    /**
     * 번호로 원본(지금 그대로의) 룬을 되찾는 표.
     *
     * 고점 기준일 때 화면에 도는 룬은 `peakRune` 이 만든 **끝값 사본**이라, 그대로 룬 창이나
     * 몹 스탯에 넘기면 "지금 옵션" 과 파란 변화량이 사라진다(끝값끼리 견주니 늘 0). 그래서
     * 어느 기준이든 **보여 줄 때는 원본을 넘기고 끝값은 따로 만들어** 견준다. 고른 여섯 개가
     * 무엇이냐만 기준에 따라 달라진다.
     */
    const rawById = useMemo(() => new Map((runes || []).map((r) => [r.id, r])), [runes]);
    const asIs = useCallback((list: Rune[]) => list.map((r) => rawById.get(r.id) || r), [rawById]);

    const power = useMemo(() => {
        // 드데조가 켜져 있으면 몹 하나짜리는 안 센다(셈이 1초쯤 든다)
        if (!powerRunes || !meta || ddj) return null;
        return bestPowerBuild(powerRunes, POWER_COMBOS[0], meta.bonus, ARTIFACT_ATK_DEFAULT, powerRelicAtkPct, mobBaseOf(powerUnit));
    }, [powerRunes, meta, powerRelicAtkPct, powerUnit, ddj]);
    /**
     * 고른 여섯 개의 전투력 **두 값** — `now` 는 룬을 지금 그대로, `peak` 는 풀강화·변환·풀연마
     * 했을 때. 화면에는 "현재 N · 최종 N" 으로 나란히 적는다.
     *
     * 예전에는 한 값에 파란 (+N) 만 붙였는데, 기준에 따라 그 한 값이 지금 값이었다 끝값이었다
     * 해서 아래 몹 스탯과 어긋나 보였다. 두 값을 다 적으면 `now` 가 몹 스탯과 맞는다.
     */
    const powerPair = useMemo(() => {
        if (!power || !meta || !runes) return null;
        const mob = mobBaseOf(powerUnit);
        const at = (list: Rune[]) => powerOf(list, meta.bonus, ARTIFACT_ATK_DEFAULT, powerRelicAtkPct, mob).power;
        return { now: at(asIs(power.runes)), peak: at(power.runes.map((r) => peakRune(r, 'power'))) };
    }, [power, meta, runes, powerRelicAtkPct, powerUnit, asIs]);

    /**
     * 드데조 셋 — 룬이 겹치지 않고 **풍조커·빛데몬이 풍드나보다 느리지 않게**, 셋의 전투력 합이 가장 높은 쪽(runeEngine
     * `ddjBuilds`). 몹마다 그 몹의 밑값·유물·기본속도로 센다. 계정에 그 몹이 없으면 밑값은 조커, 기본속도는 100 으로 대신한다.
     */
    const ddjBuilds = useMemo(() => {
        if (!ddj || !powerRunes || !meta) return null;
        const owned = DDJ_MOBS.map((m) => {
            const id = ownedUnitId(units, m.family, m.attribute);
            return id != null ? (units.find((x) => x.unitId === id) || null) : null;
        });
        const solved = ddjSolve(powerRunes, owned.map((u) => ({
            base: mobBaseOf(u), spd: u ? u.base.spd : 100, relicAtkPct: u ? relicMains(u.relics).atkPct : 0
        })), meta.bonus, ARTIFACT_ATK_DEFAULT);
        return DDJ_MOBS.map((m, k) => {
            const u = owned[k];
            const build = solved.builds[k];
            const relicPct = u ? relicMains(u.relics).atkPct : 0;
            const at = (list: Rune[]) => powerOf(list, meta.bonus, ARTIFACT_ATK_DEFAULT, relicPct, mobBaseOf(u)).power;
            const pair = build ? { now: at(asIs(build.runes)), peak: at(build.runes.map((r) => peakRune(r, 'power'))) } : null;
            return { ...m, unit: u, build, pair, speed: solved.speeds[k] };
        });
        // ownedUnitId 는 idIndex(몹 표)에 기대므로 표가 바뀌면 다시 센다
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ddj, powerRunes, meta, units, idIndex, asIs]);

    const pickMob = (family: string, attribute: string) => {
        if (view === 'fit') return;
        setPicks((cur) => ({ ...cur, [view]: { family, attribute, unitId: ownedUnitId(units, family, attribute) } }));
    };

    const combo = SPEED_PICKS.find((c) => c.key === comboKey) || SPEED_PICKS[0];
    const speed = useMemo(() => {
        if (!speedRunes) return null;
        return {
            // 1~10순위 (사용자 2026-10-21 — 예전엔 셋)
            ranks: rankSpeed(speedRunes, combo, base, 10, mains),
            above: thresholdsOf(combo).map((th) => ({ th, n: setsAbove(speedRunes, combo, base, th, 12, mains).length }))
        };
    }, [speedRunes, combo, base, mains]);
    const speedBuild: SpeedBuild | null = speed ? (speed.ranks[rank] || speed.ranks[0] || null) : null;
    /** 전투력과 같은 짜임 — 이 여섯 개의 지금 속도와 끝 속도 */
    const speedPair = useMemo(() => {
        if (!speedBuild || !runes) return null;
        return {
            now: speedTotalOf(asIs(speedBuild.runes), combo, base),
            peak: speedTotalOf(speedBuild.runes.map((r) => peakRune(r, 'speed')), combo, base)
        };
    }, [speedBuild, runes, combo, base, asIs]);

    const pad = isMobile ? 'clamp(12px, 3vw, 16px)' : '20px 24px';
    const h2: React.CSSProperties = { margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 900, color: '#333' };
    const sub: React.CSSProperties = { fontSize: isMobile ? 11 : 12, color: T.sub, marginTop: 4, lineHeight: 1.6 };

    return (
        <div style={{
            width: '100%', minHeight: 'calc(100vh - 60px)', background: T.bg, color: T.text,
            padding: isMobile ? 'clamp(12px, 3vw, 20px) clamp(6px, 2vw, 12px)' : '24px 20px'
        }}>
            <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>

                {/* ── 머리말 — 제목은 다른 탭과 같은 모양(가운데, 보라) ── */}
                <div style={{ ...card, padding: pad }}>
                    <h2 style={{
                        margin: 0, fontSize: isMobile ? 'clamp(18px, 4vw, 22px)' : 26, fontWeight: 800,
                        color: '#667eea', textAlign: 'center'
                    }}>룬 옵티마이저</h2>

                    <input
                        ref={fileInput} type="file"
                        /*
                         * MIME 을 **앞에** 둔다 — 안드로이드 크롬은 목록의 첫 항목만 보고 고르기 창을
                         * 여는 때가 있어, 확장자가 앞이면 걸러지지 않고 모든 파일이 나왔다.
                         */
                        accept="application/json,.json"
                        style={{ display: 'none' }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); }}
                    />

                    {problem && <div style={{ marginTop: 10, color: T.red, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>{problem}</div>}

                    {/*
                      읽어 둔 계정 — 한 줄에 하나씩. 예전에는 알약(Chip) 넷이 한 줄에 붙어 있어
                      무엇이 무엇인지 읽기 어려웠다.
                    */}
                    {runes && meta && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left' }}>
                            <InfoRow label="닉네임" value={meta.wizardName || '이름 없음'} isMobile={isMobile} />
                            <InfoRow label="룬" value={`${runes.length.toLocaleString()}개 (착용 ${meta.equipped.toLocaleString()} · 창고 ${meta.stored.toLocaleString()})`} isMobile={isMobile} />
                            {savedAt && (
                                <InfoRow
                                    label="업로드 날짜"
                                    value={new Date(savedAt).toLocaleString('ko-KR', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    isMobile={isMobile}
                                />
                            )}
                        </div>
                    )}

                    {/* 알약 단추 줄은 계정이 있을 때만 — 없을 때는 아래 점선 [+] 칸이 그 몫을 한다 */}
                    {runes && (
                        <div style={{ marginTop: 12, display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => fileInput.current?.click()} disabled={reading}
                                title="SWEX 가 저장한 계정이름-번호.json"
                                style={{ ...button(!reading), padding: isMobile ? '8px 16px' : '9px 20px', borderRadius: 999 }}
                            >
                                {reading ? '읽는 중…' : '다른 JSON 선택'}
                            </button>
                            <button
                                onClick={() => { clearAccount(); clearRawAccount(); setRunes(null); setUnits([]); setMeta(null); setSavedAt(null); setPicks({ power: null, speed: null }); }}
                                style={{ border: 'none', background: 'transparent', color: '#999', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                            >지우기</button>
                        </div>
                    )}
                </div>

                {/*
                  계정이 없을 때 — 설명 한 줄 대신 **하늘색 점선 [+] 칸**. 눌러도 되고 파일을
                  끌어다 놓아도 된다. 무엇을 해야 하는지가 글보다 한눈에 들어온다.

                  바탕은 **그냥 흰색**이다. 반투명으로 뒀더니 뒤 바탕이 비쳐 점선이 묻혔다.
                  칸을 좁혀 보기도 했지만 그래도 안 보여서, 크기는 도로 카드 너비로 돌렸다.
                */}
                {!runes && (
                    <button
                        onClick={() => fileInput.current?.click()} disabled={reading}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
                        title="SWEX 가 저장한 계정이름-번호.json"
                        style={{
                            ...card, width: '100%', boxSizing: 'border-box',
                            padding: isMobile ? '32px 16px' : '44px 24px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                            border: `2px dashed ${dragging ? '#2f9ae4' : '#6bbcee'}`,
                            background: dragging ? '#eef7fd' : '#fff',
                            boxShadow: 'none', color: '#3f9ad6', fontFamily: 'inherit',
                            cursor: reading ? 'default' : 'pointer', transition: 'background 0.15s, border-color 0.15s'
                        }}
                    >
                        <span style={{ fontSize: isMobile ? 36 : 46, fontWeight: 300, lineHeight: 1 }}>+</span>
                        <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 800 }}>{reading ? '읽는 중…' : 'JSON 추가'}</span>
                    </button>
                )}

                {runes && (
                    <div style={{ ...card, padding: 'clamp(10px, 2vw, 12px)' }}>
                        {/* ── 룬피팅 | 전투력 | 최고속 — 전투기록 탭의 두 칸 탭과 같은 짜임 (룬피팅은 2026-10-27 맨 왼쪽에) ── */}
                        <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.line}`, marginBottom: 12 }}>
                            {([['fit', '룬피팅'], ['power', '전투력'], ['speed', '최고속']] as Array<[View, string]>).map(([k, label]) => {
                                const on = view === k;
                                return (
                                    <button
                                        key={k} onClick={() => setView(k)}
                                        style={{
                                            flex: 1, border: 'none', padding: isMobile ? '9px 0' : '10px 0', cursor: 'pointer',
                                            fontSize: isMobile ? 13 : 14, fontWeight: 900,
                                            background: on ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                            color: on ? '#fff' : '#667eea'
                                        }}
                                    >{label}</button>
                                );
                            })}
                        </div>

                        {view === 'fit' && <RuneFit source={fitSource} isMobile={isMobile} monsterMapping={monsterMapping} />}

                        {/* 현재 기준 | 고점 기준 */}
                        {view !== 'fit' && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 12px', padding: isMobile ? '0 2px' : '0 6px' }}>
                            <div style={{ display: 'flex', borderRadius: 999, overflow: 'hidden', border: `1px solid ${T.line}` }}>
                                {([['now', '현재 기준'], ['peak', '고점 기준']] as Array<[Basis, string]>).map(([k, label]) => {
                                    const on = basis === k;
                                    return (
                                        <button
                                            key={k} onClick={() => setBasis(k)}
                                            style={{
                                                border: 'none', padding: isMobile ? '6px 12px' : '6px 14px', cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 800,
                                                background: on ? T.blue : 'transparent', color: on ? '#fff' : '#667eea'
                                            }}
                                        >{label}</button>
                                    );
                                })}
                            </div>
                            <span style={{ fontSize: 11, color: T.sub, fontWeight: 600 }}>
                                {basis === 'peak'
                                    ? '풀강화 · 보석 · 연마 적용 최고점 전투력 기준'
                                    : '현재 있는 룬 기준'}
                            </span>
                            {/* 드데조 — 전투력 탭에서만, 기준 고르기와 같은 줄 오른쪽 끝(사용자 2026-10-21) */}
                            {view === 'power' && (
                                <button
                                    onClick={() => setDdj((v) => !v)}
                                    style={{ marginLeft: 'auto', ...button(true, ddj ? T.blue : '#fff'), color: ddj ? '#fff' : '#667eea', border: `1px solid ${ddj ? T.blue : 'rgba(102,126,234,0.35)'}`, padding: '7px 14px', borderRadius: 999, fontSize: isMobile ? 12 : 13 }}
                                >드데조 {ddj ? 'ON' : 'OFF'}</button>
                            )}
                        </div>}

                        {view === 'power' && ddj && ddjBuilds && (() => {
                            // 칸은 드나 · 데몬 · 조커 순으로 놓는다(사용자 2026-10-21)
                            const order = [1, 2, 0];
                            const d = ddjBuilds[ddjPick] || ddjBuilds[0];
                            return (
                                <div style={{ padding: isMobile ? '0 2px' : '0 6px' }}>
                                    <div style={{ display: 'flex', gap: isMobile ? 8 : 10, marginBottom: 14 }}>
                                        {order.map((k) => {
                                            const b = ddjBuilds[k];
                                            const on = k === ddjPick;
                                            const img = mobImage(b.family, b.attribute);
                                            return (
                                                <button
                                                    key={k} onClick={() => setDdjPick(k)}
                                                    style={{
                                                        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                                                        padding: isMobile ? '8px 4px' : '10px 6px', borderRadius: 14, cursor: 'pointer',
                                                        background: on ? 'rgba(102,126,234,0.10)' : '#fff',
                                                        border: on ? `2px solid ${T.blue}` : '1px solid rgba(102,126,234,0.25)'
                                                    }}
                                                >
                                                    {img
                                                        ? <img src={img} alt={b.label} style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, objectFit: 'contain', background: '#f8faff' }} />
                                                        : <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, background: '#eee' }} />}
                                                    <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 900, color: on ? T.blue : '#555' }}>{b.label}</span>
                                                    <span style={{ fontSize: 10, fontWeight: 700, color: T.sub }}>{b.pair ? `${b.pair.now}` : '-'}{b.speed != null ? ` · 속도 ${b.speed}` : ''}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 900, color: '#333' }}>전투력</span>
                                        {d.build && <Pair now={d.pair?.now} peak={d.pair?.peak} isMobile={isMobile} />}
                                    </div>
                                    {!d.build && <div style={{ marginTop: 8, color: '#bbb', fontSize: 12, fontWeight: 600 }}>남은 룬으로는 격노 4세트 + 칼날 2세트를 채울 수 없습니다</div>}
                                    {d.build && (
                                        <div style={{ marginTop: isMobile ? 10 : 0 }}>
                                            <StatRuneSwitch
                                                isMobile={isMobile}
                                                stat={
                                                    <div style={{ marginTop: isMobile ? 0 : 10, display: 'grid' }}>
                                                        <MonsterStatPanel
                                                            unit={d.unit} runes={asIs(d.build.runes)} peakRunes={d.build.runes.map((r) => peakRune(r, 'power'))}
                                                            name={mobShort(d.family, d.attribute)} img={mobImage(d.family, d.attribute)}
                                                            isMobile={isMobile}
                                                        />
                                                    </div>
                                                }
                                                rune={<RuneBuildPanel key={`ddj-${ddjPick}-${basis}`} runes={asIs(d.build.runes)} objective="power" relic={d.unit?.relics[0] || null} isMobile={isMobile} />}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {view === 'power' && !ddj && (
                            <div style={{ padding: isMobile ? '0 2px' : '0 6px' }}>
                                {!power && <div style={{ marginTop: 10, color: '#bbb', fontSize: 12, fontWeight: 600 }}>격노 4세트 + 칼날 2세트로 여섯 자리를 채울 룬이 없습니다</div>}
                                {power && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: isMobile ? 14 : 15, fontWeight: 900, color: '#333' }}>전투력</span>
                                            <Pair now={powerPair?.now} peak={powerPair?.peak} isMobile={isMobile} />
                                        </div>
                                        <div style={{ marginTop: pick && isMobile ? 10 : 0 }}>
                                        <StatRuneSwitch
                                            isMobile={isMobile && !!pick}
                                            stat={pick && (
                                                <div style={{ marginTop: isMobile ? 0 : 10, display: 'grid' }}>
                                                    <MonsterStatPanel
                                                        unit={unit} runes={asIs(power.runes)} peakRunes={power.runes.map((r) => peakRune(r, 'power'))}
                                                        name={mobShort(pick.family, pick.attribute)} img={mobImage(pick.family, pick.attribute)}
                                                        isMobile={isMobile} onPortraitClick={() => setPickerOpen(true)}
                                                    />
                                                </div>
                                            )}
                                            rune={<RuneBuildPanel key={`power-${basis}`} runes={asIs(power.runes)} objective="power" relic={unit?.relics[0] || null} isMobile={isMobile} />}
                                        />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {view === 'speed' && (
                            <div style={{ padding: isMobile ? '0 2px' : '0 6px' }}>
                                {/* 조합 고르기 — 제목·설명·기본속도 단추는 뺐다(사용자 지시). 기본속도는 115 로 고정 */}
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {SPEED_PICKS.map((c) => {
                                        const on = c.key === combo.key;
                                        return (
                                            <button
                                                key={c.key} onClick={() => { setComboKey(c.key); setRank(0); }}
                                                style={{ ...button(true, on ? T.blue : '#fff'), color: on ? '#fff' : '#667eea', border: `1px solid ${on ? T.blue : 'rgba(102,126,234,0.35)'}`, padding: '7px 14px', borderRadius: 999, fontSize: isMobile ? 12 : 13 }}
                                            >{c.label.split(' ')[0]}</button>
                                        );
                                    })}
                                </div>

                                {/* 4번·6번 주옵 — 전체 또는 하나 (2번은 늘 공속) */}
                                {([[4, SLOT4_MAINS], [6, SLOT6_MAINS]] as Array<[4 | 6, number[]]>).map(([slot, list]) => (
                                    <div key={slot} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                                        <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 900, color: '#555', width: 30 }}>{slot}번</span>
                                        {([null, ...list] as Array<number | null>).map((eff) => {
                                            const on = (mains[slot] ?? null) === eff;
                                            return (
                                                <button
                                                    key={String(eff)} onClick={() => { setMains((cur) => ({ ...cur, [slot]: eff })); setRank(0); }}
                                                    style={{ ...button(true, on ? T.blue : '#fff'), color: on ? '#fff' : '#667eea', border: `1px solid ${on ? T.blue : 'rgba(102,126,234,0.35)'}`, padding: '5px 10px', borderRadius: 999, fontSize: isMobile ? 11 : 12 }}
                                                >{eff == null ? '전체' : MAIN_SHORT[eff]}</button>
                                            );
                                        })}
                                    </div>
                                ))}

                                {speed && speed.ranks.length === 0 && (
                                    <div style={{ marginTop: 12, color: '#bbb', fontSize: 12, fontWeight: 600 }}>이 조합·주옵으로 여섯 자리를 채울 룬이 없습니다</div>
                                )}

                                {speed && speedBuild && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                                            {/* 순위 — 열 개라 좁으면 다음 줄로 */}
                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                {speed.ranks.map((_, i) => (
                                                    <button key={i} onClick={() => setRank(i)} style={{ ...button(true, rank === i ? T.blue : '#b0bec5'), padding: '5px 10px', fontSize: 11 }}>{i + 1}순위</button>
                                                ))}
                                            </div>
                                            <Pair now={speedPair?.now} peak={speedPair?.peak} isMobile={isMobile} plus />
                                        </div>
                                        <div style={{ marginTop: pick && isMobile ? 10 : 0 }}>
                                        <StatRuneSwitch
                                            isMobile={isMobile && !!pick}
                                            stat={pick && (
                                                <div style={{ marginTop: isMobile ? 0 : 10, display: 'grid' }}>
                                                    <MonsterStatPanel
                                                        unit={unit} runes={asIs(speedBuild.runes)} peakRunes={speedBuild.runes.map((r) => peakRune(r, 'speed'))}
                                                        name={mobShort(pick.family, pick.attribute)} img={mobImage(pick.family, pick.attribute)}
                                                        isMobile={isMobile} onPortraitClick={() => setPickerOpen(true)}
                                                    />
                                                </div>
                                            )}
                                            rune={<RuneBuildPanel key={`${combo.key}-${rank}-${base}-${basis}`} runes={asIs(speedBuild.runes)} objective="speed" relic={unit?.relics[0] || null} isMobile={isMobile} />}
                                        />
                                        </div>
                                        {/* 기준 속도 이상으로 겹치지 않게 몇 세트 — 화면 낱말은 "벌" 대신 "세트"(사용자 지시) */}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                                            {speed.above.map(({ th, n }) => (
                                                <Chip key={th} muted>{th} 이상 {n}세트</Chip>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 사이트의 몹 고르기 창 — 전적검색에서 쓰는 그것 */}
            {pickerOpen && (
                <PickerModal
                    isMobile={isMobile}
                    monsterMapping={monsterMapping}
                    onClose={() => setPickerOpen(false)}
                    onPick={(_short, _base, _imgPath, attribute, _leader, _skills, family) => pickMob(family, attribute)}
                />
            )}
        </div>
    );
}

// ── 조각들 ─────────────────────────────────────────────────────────────────

/**
 * **현재 N · 최종 N** — 지금 룬 그대로의 값과 풀강화·변환·풀연마했을 때의 값을 나란히.
 * `plus` 면 속도처럼 앞에 + 를 붙인다. 두 값이 같으면 "최종" 은 접는다.
 */
function Pair({ now, peak, isMobile, plus = false }: { now?: number; peak?: number; isMobile: boolean; plus?: boolean }) {
    if (now == null || peak == null) return null;
    const big = isMobile ? 20 : 24;
    const tag: React.CSSProperties = { fontSize: 11, color: '#999', fontWeight: 800 };
    const show = (v: number) => `${plus ? '+' : ''}${v.toLocaleString()}`;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={tag}>현재</span>
            <span style={{ fontSize: big, fontWeight: 900, color: T.blue }}>{show(now)}</span>
            {peak !== now && (
                <>
                    <span style={{ ...tag, marginLeft: 6 }}>최종</span>
                    <span style={{ fontSize: big, fontWeight: 900, color: '#5ba7f0' }}>{show(peak)}</span>
                </>
            )}
        </span>
    );
}

/** 읽어 둔 계정을 한 줄에 하나씩 — 이름표는 옅게, 값은 사이트 보라로 */
function InfoRow({ label, value, isMobile }: { label: string; value: string; isMobile: boolean }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: isMobile ? 12 : 13 }}>
            <span style={{ minWidth: isMobile ? 66 : 76, color: '#a0a4b8', fontWeight: 700, flexShrink: 0 }}>{label}</span>
            <span style={{ color: '#4a5bc4', fontWeight: 800 }}>{value}</span>
        </div>
    );
}

function Chip({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
    return (
        <span style={{
            padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
            background: muted ? 'rgba(0,0,0,0.05)' : 'rgba(102, 126, 234, 0.12)',
            color: muted ? '#777' : '#4a5bc4'
        }}>{children}</span>
    );
}
